import { useEffect, useRef } from 'react';

const STORE_KEYWORDS = {
  Coles: 'coles',
  Woolworths: 'woolworths',
  Aldi: 'aldi',
};

function storeStatus(store, logs) {
  const key = STORE_KEYWORDS[store];
  const relevant = logs.filter(l => l.message.toLowerCase().includes(key));
  if (relevant.length === 0) return 'pending';
  const last = relevant[relevant.length - 1].message;
  if (last.includes('✓') || last.includes('Done')) return 'done';
  if (last.includes('✗') || last.includes('Error') || last.includes('⚠')) return 'error';
  return 'running';
}

function storeCount(store, logs) {
  const key = STORE_KEYWORDS[store];
  const done = logs.find(l =>
    l.message.toLowerCase().includes(key) &&
    (l.message.includes('Done') || l.message.includes('✓'))
  );
  if (!done) return null;
  const m = done.message.match(/(\d+)\s+(half-price deals|specials)/);
  return m ? parseInt(m[1], 10) : null;
}

function StatusIcon({ status }) {
  if (status === 'pending') return <span className="sp-icon sp-icon--pending">○</span>;
  if (status === 'running') return <span className="sp-icon sp-icon--running"><span className="sp-spinner" /></span>;
  if (status === 'done')    return <span className="sp-icon sp-icon--done">✓</span>;
  if (status === 'error')   return <span className="sp-icon sp-icon--error">✗</span>;
  return null;
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function ScrapeProgress({ status, onCancel }) {
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [status?.logs?.length]);

  if (!status || (!status.inProgress && !status.logs?.length)) return null;

  const stores = ['Coles', 'Woolworths', 'Aldi'];

  return (
    <div className="sp-panel">
      <div className="sp-header">
        <div className="sp-title">
          {status.inProgress && <span className="sp-pulse-dot" />}
          {status.inProgress ? 'Scraping in progress…' : 'Last scrape complete'}
          {status.startedAt && (
            <span className="sp-started">Started {formatTime(status.startedAt)}</span>
          )}
        </div>
        {status.inProgress && onCancel && (
          <button className="sp-btn-cancel" onClick={onCancel}>Cancel</button>
        )}
      </div>

      <div className="sp-stores">
        {stores.map(store => {
          const s = storeStatus(store, status.logs);
          const count = storeCount(store, status.logs);
          return (
            <div key={store} className={`sp-store sp-store--${s}`}>
              <StatusIcon status={s} />
              <span className="sp-store-name">{store}</span>
              {count !== null && (
                <span className="sp-store-count">{count} deals</span>
              )}
              {s === 'running' && <span className="sp-store-label">Running…</span>}
              {s === 'pending' && <span className="sp-store-label muted">Queued</span>}
            </div>
          );
        })}
      </div>

      <div className="sp-log" ref={logRef}>
        {status.logs.map((entry, i) => {
          const isOk   = entry.message.includes('✓') || entry.message.includes('✅');
          const isWarn = entry.message.includes('✗') || entry.message.includes('⚠');
          const isApi  = entry.message.includes('API response captured');
          return (
            <div key={i} className={`sp-log-line ${isOk ? 'sp-log-line--ok' : isWarn ? 'sp-log-line--warn' : isApi ? 'sp-log-line--api' : ''}`}>
              <span className="sp-log-time">{formatTime(entry.time)}</span>
              <span className="sp-log-msg">{entry.message}</span>
            </div>
          );
        })}
        {status.inProgress && (
          <div className="sp-log-line sp-log-line--waiting">
            <span className="sp-log-time"> </span>
            <span className="sp-log-msg sp-blink">▌</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ScrapeProgress;
